import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { AlertTriangle, Clock, Download, Eye, FileText, MessageCircle, Paperclip, Receipt, RefreshCw, Shield, Truck, Users, CreditCard } from "lucide-react";
import { toast } from "sonner";

export const DepositForm = ({ register, setValue, formValues, customers, handleViewInvoice, handleDownloadInvoice, handleAttachInvoice }) => (
  <>
    {/* Sync Button */}
    <Card className="border-2 border-purple-300 bg-purple-50">
      <CardContent className="p-3">
        <Button 
          onClick={() => {
            // Sync customer details from quotation
            setValue("deposit_moving_from_full", formValues.moving_from);
            setValue("deposit_moving_to_full", formValues.moving_to);
            // Map package prices to selected package
            const prices = {
              man_and_van: formValues.man_and_van_price,
              economy: formValues.economy_move_price,
              comfort: formValues.comfort_move_price,
              comfort_plus: formValues.comfort_plus_price,
              premium: formValues.premium_price,
            };
            setValue("deposit_total_amount", prices[formValues.deposit_selected_package] || formValues.comfort_move_price);
            toast.success("Customer details synced from quotation!");
          }}
          variant="outline"
          className="w-full h-9 border-purple-400 text-purple-700 hover:bg-purple-100"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Sync Details from Quote
        </Button>
        <p className="text-xs text-purple-600 mt-1 text-center">Copy customer info from quotation tab</p>
      </CardContent>
    </Card>

    <Card className="border-2 border-emerald-300">
      <div className="bg-emerald-500 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <Receipt className="h-4 w-4" />
          Deposit Details
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div>
          <Label className="text-xs">Selected Package</Label>
          <Select value={formValues.deposit_selected_package} onValueChange={(val) => setValue("deposit_selected_package", val)}>
            <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="loading_crew">Loading Crew Only</SelectItem>
              <SelectItem value="man_and_van">Man & Van</SelectItem>
              <SelectItem value="economy">Economy Move</SelectItem>
              <SelectItem value="comfort">Comfort Move</SelectItem>
              <SelectItem value="comfort_plus">Complete Comfort Move</SelectItem>
              <SelectItem value="premium">Premium Move</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Total Amount (£)</Label>
            <Input {...register("deposit_total_amount")} className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">Deposit Received (£)</Label>
            <Input {...register("deposit_received_amount")} className="h-9 mt-1 bg-green-50 border-green-300" />
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-2 border-gray-300">
      <div className="bg-gray-700 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4" />
          Move Schedule
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div>
          <Label className="text-xs font-semibold">Move Date</Label>
          <Input 
            {...register("moving_date")} 
            placeholder="e.g. Friday, 15 March 2026"
            className="h-12 mt-1 border-2 border-blue-400 focus:border-blue-600 font-semibold text-base"
            style={{ fontSize: '16px' }}
            data-testid="deposit-moving-date"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold">Arrival Time</Label>
          <Input 
            {...register("arrival_time")} 
            placeholder="8:00 AM - 9:00 AM" 
            className="h-12 mt-1 border-2 border-gray-300 focus:border-blue-500 font-semibold"
            style={{ fontSize: '16px' }}
          />
        </div>
      </CardContent>
    </Card>

    {/* CREW UPGRADE RECOMMENDATION - Deposit Tab */}
    <Card className="border-2 border-amber-400 bg-amber-50" data-testid="crew-recommendation-deposit">
      <div className="bg-amber-500 px-3 py-2 flex items-center justify-between">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <Users className="h-4 w-4" />
          Crew Upgrade Recommendation
        </h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-white font-medium">Show in Email</span>
          <input 
            type="checkbox" 
            {...register("show_crew_recommendation")}
            className="h-4 w-4 rounded border-white"
          />
        </label>
      </div>
      {formValues.show_crew_recommendation && (
        <CardContent className="p-3 space-y-3">
          <div className="bg-white rounded-lg p-3 border border-amber-300">
            <p className="text-sm text-gray-700 mb-3 font-medium">
              This Quote Includes:
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <Label className="text-xs font-semibold text-amber-800">Crew Size</Label>
                <Input 
                  {...register("booked_crew_count")} 
                  placeholder="2"
                  className="h-10 mt-1 border-amber-300 font-bold text-center"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-amber-800">Van Type</Label>
                <Input 
                  {...register("booked_van_type")} 
                  placeholder="3.5T Van"
                  className="h-10 mt-1 border-amber-300 font-bold text-center"
                />
              </div>
            </div>
          </div>
          
          <div className="bg-green-100 rounded-lg p-3 border-2 border-green-400">
            <p className="text-sm text-green-800 font-semibold mb-2">
              💡 For a faster & smoother move we recommend:
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold text-green-700">Recommended Crew</Label>
                <Input 
                  {...register("recommended_crew_count")} 
                  placeholder="3"
                  className="h-10 mt-1 border-green-400 font-bold text-center bg-white"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-green-700">Recommended Van</Label>
                <Input 
                  {...register("recommended_van_type")} 
                  placeholder="7.5T Truck"
                  className="h-10 mt-1 border-green-400 font-bold text-center bg-white"
                />
              </div>
            </div>
            
            <div className="mt-3 bg-green-200 rounded-lg p-2 text-center">
              <Label className="text-xs font-semibold text-green-800">Extra Cost for Upgrade</Label>
              <div className="flex items-center justify-center gap-1 mt-1">
                <span className="text-lg font-bold text-green-800">+£</span>
                <Input 
                  {...register("crew_upgrade_cost")} 
                  placeholder="150"
                  className="h-10 w-20 border-green-500 font-bold text-xl text-center bg-white"
                />
              </div>
            </div>
            
            <p className="text-xs text-green-700 mt-2 text-center">
              ⏱️ Save time & reduce stress with extra help!
            </p>
          </div>
        </CardContent>
      )}
    </Card>

    {/* CUSTOM MESSAGE TO CUSTOMER - Deposit Tab */}
    <Card className="border-2 border-purple-400 bg-purple-50" data-testid="custom-recommendation-deposit">
      <div className="bg-purple-600 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <MessageCircle className="h-4 w-4" />
          Your Message to Customer
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <p className="text-xs text-purple-700">Write a personal recommendation or message to your customer:</p>
        
        <Textarea 
          {...register("custom_recommendation_message")} 
          placeholder="Example: You booked with us 2 crew members for your job. If you want a faster and smoother moving experience, we recommend booking one more professional mover. This will help reduce the time and make your move stress-free!

Or: Based on the size of your property, we suggest adding packing materials to protect your fragile items during transit."
          className="min-h-[120px] text-sm border-2 border-purple-300 focus:border-purple-500"
          style={{ fontSize: '15px' }}
        />
        
        {/* Quick Templates */}
        <div>
          <Label className="text-xs font-semibold text-purple-700 mb-2 block">Quick Templates (tap to use):</Label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `You booked with us ${formValues.booked_crew_count || '2'} crew members and a ${formValues.booked_van_type || '3.5T Van'} for your move. For a faster and smoother experience, we recommend upgrading to ${formValues.recommended_crew_count || '3'} professional movers. This will significantly reduce loading/unloading time and ensure your belongings are handled with extra care!`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              🚚 Crew Upgrade
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `We noticed your move includes fragile items. We highly recommend our professional packing service to ensure everything arrives safely. Our team uses high-quality packing materials and techniques to protect your valuables during transit.`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              📦 Packing Service
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `Based on the distance and property size, we recommend booking our premium insurance cover (up to £50,000) for complete peace of mind. This covers any accidental damage during the move.`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              🛡️ Insurance
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `Thank you for choosing HomeGo Removals! As a reminder, please ensure all items are boxed and ready on the moving day. Our team will handle the heavy lifting, furniture dismantling, and reassembly at your new home. See you soon!`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              👋 Friendly Reminder
            </button>
            <button
              type="button"
              onClick={() => setValue("custom_recommendation_message", 
                `We understand moving can be stressful, especially if you'd like to take it easy or have mobility concerns. For your peace of mind, we offer a full packing service where our professional team will carefully pack ALL your belongings - from fragile china to heavy books. You just relax and let us handle everything! This is perfect for elderly customers, busy professionals, or anyone who wants a completely stress-free move. Ask us about adding packing to your booking!`
              )}
              className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs rounded-full border border-purple-300"
            >
              👴 Packing for Elderly/Relaxed Move
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input 
            type="checkbox" 
            {...register("show_custom_message")}
            className="h-4 w-4 rounded border-gray-300"
          />
          <Label className="text-xs">Show this message in email</Label>
        </div>
      </CardContent>
    </Card>

    <Card className="border-2 border-gray-300">
      <div className="bg-gray-100 px-3 py-2 border-b border-gray-300">
        <h3 className="font-bold text-gray-700 flex items-center gap-2 text-sm">
          <Truck className="h-4 w-4" />
          Move Locations
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <div>
          <Label className="text-xs font-semibold">Moving From (Full Address)</Label>
          <Input 
            {...register("deposit_moving_from_full")} 
            placeholder="123 High Street, London, E1 6AN" 
            className="h-12 mt-1 border-2 border-blue-400 focus:border-blue-600 font-semibold text-base"
            style={{ fontSize: '16px' }}
            data-testid="deposit-moving-from"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold">Moving To (Full Address)</Label>
          <Input 
            {...register("deposit_moving_to_full")} 
            placeholder="456 Oak Road, Manchester, M1 2AB" 
            className="h-12 mt-1 border-2 border-blue-400 focus:border-blue-600 font-semibold text-base"
            style={{ fontSize: '16px' }}
            data-testid="deposit-moving-to"
          />
        </div>
      </CardContent>
    </Card>

    <Card className="border-2 border-blue-300">
      <div className="bg-blue-500 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4" />
          What's Next?
        </h3>
      </div>
      <CardContent className="p-3">
        <Textarea 
          {...register("deposit_whats_next")} 
          placeholder="Enter each point on a new line..." 
          className="min-h-[100px] text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">Each line becomes a bullet point</p>
      </CardContent>
    </Card>

    <Card className="border-2 border-yellow-300">
      <div className="bg-yellow-500 px-3 py-2">
        <h3 className="font-bold text-black flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4" />
          Our Commitment
        </h3>
      </div>
      <CardContent className="p-3">
        <Textarea 
          {...register("deposit_our_commitment")} 
          placeholder="Enter each commitment on a new line..." 
          className="min-h-[100px] text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">Each line becomes a checkmark point</p>
      </CardContent>
    </Card>

    <Card className="border-2 border-red-300">
      <div className="bg-red-500 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4" />
          Important Notes
        </h3>
      </div>
      <CardContent className="p-3">
        <Textarea 
          {...register("deposit_important_notes")} 
          placeholder="Enter important notes on each line..." 
          className="min-h-[120px] text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">Parking, keys, contact info etc.</p>
      </CardContent>
    </Card>

    <Card className="border-2 border-green-300">
      <div className="bg-green-600 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <CreditCard className="h-4 w-4" />
          Payment Link (Optional)
        </h3>
      </div>
      <CardContent className="p-3">
        <Input 
          {...register("deposit_payment_link")} 
          placeholder="https://payment-link.com/..." 
          className="h-9"
        />
        <p className="text-xs text-gray-500 mt-1">Add link to show "Pay Full Balance Now" button</p>
      </CardContent>
    </Card>

    {/* Invoice Editor - Deposit */}
    <Card className="border-2 border-emerald-400 bg-emerald-50" data-testid="invoice-editor-deposit">
      <div className="bg-emerald-600 px-3 py-2">
        <h3 className="font-bold text-white flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4" />
          Deposit Invoice Settings
        </h3>
      </div>
      <CardContent className="p-3 space-y-3">
        <p className="text-xs text-emerald-700">These fields customize the Deposit Invoice PDF</p>
        
        {/* Package Selector */}
        <div>
          <Label className="text-xs font-semibold">Select Package</Label>
          <select 
            className="w-full h-9 mt-1 px-3 border rounded-md text-sm"
            value={formValues.invoice_service_name}
            onChange={(e) => {
              const pkg = e.target.value;
              setValue("invoice_service_name", pkg);
              // Auto-fill description based on package
              const descriptions = {
                "Man & Van": "1 Driver + Van, Loading assistance",
                "Economy Move": "Loading & unloading, placing boxes in chosen rooms",
                "Comfort Move": "Dismantling & reassembling furniture, Loading & unloading, Placing boxes in correct rooms",
                "Comfort Plus": "Full packing of all items, Fragile packing, Dismantling & reassembling, Loading & unloading",
                "Premium Move": "Complete packing, Full unpacking, Furniture placement, Debris removal"
              };
              setValue("invoice_service_description", descriptions[pkg] || "");
              // Auto-fill price
              const prices = {
                "Man & Van": formValues.man_and_van_price,
                "Economy Move": formValues.economy_move_price,
                "Comfort Move": formValues.comfort_move_price,
                "Comfort Plus": formValues.comfort_plus_price,
                "Premium Move": formValues.premium_price
              };
              setValue("invoice_move_price", prices[pkg] || formValues.comfort_move_price);
            }}
          >
            <option value="Man & Van">Man & Van</option>
            <option value="Economy Move">Economy Move</option>
            <option value="Comfort Move">Comfort Move</option>
            <option value="Comfort Plus">Comfort Plus</option>
            <option value="Premium Move">Premium Move</option>
          </select>
        </div>

        {/* Move Price */}
        <div>
          <Label className="text-xs font-semibold">Total Move Price (£)</Label>
          <Input 
            {...register("invoice_move_price")} 
            type="number"
            placeholder="485" 
            className="h-9 mt-1" 
          />
        </div>

        {/* Deposit Amount */}
        <div>
          <Label className="text-xs font-semibold">Deposit Amount (£)</Label>
          <Input 
            {...register("invoice_deposit_amount")} 
            type="number"
            placeholder="100" 
            className="h-9 mt-1" 
          />
        </div>

        <div>
          <Label className="text-xs">Service Description</Label>
          <Textarea {...register("invoice_service_description")} placeholder="Describe what's included..." className="min-h-[60px] mt-1 text-xs" />
        </div>

        {/* Addresses */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">From Postcode</Label>
            <Input {...register("moving_from")} placeholder="MK11 4BB" className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">To Postcode</Label>
            <Input {...register("moving_to")} placeholder="MK14 7BB" className="h-9 mt-1" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Customer Phone</Label>
            <Input {...register("invoice_customer_phone")} placeholder="07xxx" className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">Customer Email</Label>
            <Input {...register("invoice_customer_email")} placeholder="email" className="h-9 mt-1" />
          </div>
        </div>
        
        {/* Invoice Action Buttons */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button 
            onClick={() => handleViewInvoice('receipt')} 
            variant="outline"
            className="h-10 border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          >
            <Eye className="h-4 w-4 mr-2" />
            View Receipt
          </Button>
          <Button 
            onClick={() => handleDownloadInvoice('receipt')} 
            className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </div>
        <Button 
          onClick={() => handleAttachInvoice('receipt')} 
          className="w-full h-10 mt-2 bg-black hover:bg-zinc-800 text-[#D4AF37] font-bold border border-[#D4AF37]"
          data-testid="attach-receipt-invoice-button"
        >
          <Paperclip className="h-4 w-4 mr-2" />
          Attach Receipt to This Email
        </Button>
      </CardContent>
    </Card>

    {/* WORKING HOURS & OVERTIME POLICY */}
    <Card className="border-2 border-gray-700 bg-gray-50" data-testid="overtime-policy-card">
      <div className="bg-gray-900 px-3 py-2 flex items-center justify-between">
        <h3 className="font-bold text-amber-400 flex items-center gap-2 text-sm">
          <Shield className="h-4 w-4" />
          Working Hours & Overtime Policy
        </h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-white font-medium">Show in Email</span>
          <input
            type="checkbox"
            {...register("show_overtime_policy")}
            data-testid="show-overtime-policy-checkbox"
            className="h-4 w-4 rounded border-white"
          />
        </label>
      </div>
      {formValues.show_overtime_policy && (
        <CardContent className="p-3 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Start Time</Label>
              <Input {...register("work_hours_start")} placeholder="9:00 AM" className="h-10 mt-1 font-bold text-center" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">End Time</Label>
              <Input {...register("work_hours_end")} placeholder="5:00 PM" className="h-10 mt-1 font-bold text-center" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-red-700">Overtime £/hour</Label>
              <Input {...register("overtime_rate")} placeholder="120" className="h-10 mt-1 border-red-300 font-bold text-center" />
            </div>
          </div>
          <p className="text-[11px] text-gray-500">Restates the working-hours and overtime terms agreed at quote stage.</p>
        </CardContent>
      )}
    </Card>
  </>
);
